import { createEquityPerpMetadata } from '../equityPerpMetadata';
import {
  AAVE_PERP_METADATA,
  ADA_PERP_METADATA,
  ARB_PERP_METADATA,
  ASTER_PERP_METADATA,
  AVAX_PERP_METADATA,
  AXS_PERP_METADATA,
  BCH_PERP_METADATA,
  BERA_PERP_METADATA,
  BNB_PERP_METADATA,
  BTC_PERP_METADATA,
  DOGE_PERP_METADATA,
  ENA_PERP_METADATA,
  ETH_PERP_METADATA,
  FARTCOIN_PERP_METADATA,
  HYPE_PERP_METADATA,
  JUP_PERP_METADATA,
  KBONK_PERP_METADATA,
  KPEPE_PERP_METADATA,
  LIT_PERP_METADATA,
  LINK_PERP_METADATA,
  LTC_PERP_METADATA,
  MON_PERP_METADATA,
  NEAR_PERP_METADATA,
  ONDO_PERP_METADATA,
  PENGU_PERP_METADATA,
  PUMP_PERP_METADATA,
  SKR_PERP_METADATA,
  SKY_PERP_METADATA,
  SOL_PERP_METADATA,
  SUI_PERP_METADATA,
  TAO_PERP_METADATA,
  UNI_PERP_METADATA,
  USELESS_PERP_METADATA,
  VIRTUAL_PERP_METADATA,
  WLFI_PERP_METADATA,
  XMR_PERP_METADATA,
  XPL_PERP_METADATA,
  XRP_PERP_METADATA,
  XAUT_PERP_METADATA,
  ZEC_PERP_METADATA,
  ZRO_PERP_METADATA,
} from '../perpMetadata';
import { PerpProductMetadata } from '../types';

// Nado on Ink mainnet uses different product ID assignments than Vertex on Arbitrum.
// This mapping is derived from the Nado API: https://gateway.prod.nado.xyz/v1/symbols
// Prices were cross-referenced against live market data (March 2026) to verify correctness.
//
// Notable differences from Vertex Arbitrum:
//   ID 8  → SOL (Arbitrum: BNB)
//   ID 14 → BNB (Arbitrum: POL)
//   ID 16 → HYPE (Arbitrum: SUI)
//   ID 18 → ZEC (Arbitrum: OP)
//   ID 20 → MON (Arbitrum: APT)
//   ID 22 → FARTCOIN (Arbitrum: LTC)
//   ID 24 → SUI (Arbitrum: BCH)
//   ID 26 → AAVE (Arbitrum: COMP)
//   ID 28 → XAUT (Arbitrum: MKR)
//   ID 30 → PUMP (Arbitrum: mPEPE)
//   ID 32 → TAO (Arbitrum: DOGE — note: DOGE moved to ID 52)
//   ID 34 → XMR (Arbitrum: DOGE)
//   ID 36 → LIT (Arbitrum: LINK)
//   ID 38 → kPEPE (Arbitrum: DYDX)
//   ID 40 → PENGU (Arbitrum: CRV)
//   ID 42 → USELESS (Arbitrum: TIA)
//   ID 44 → SKR — inactive
//   ID 46 → UNI (Arbitrum: PYTH)
//   ID 48 → ASTER (Arbitrum: JTO)
//   ID 50 → XPL (Arbitrum: JTO)
//   ID 52 → DOGE (Arbitrum: AVAX)
//   ID 54 → WLFI (Arbitrum: INJ)
//   ID 56 → kBONK (Arbitrum: SNX)
//   ID 58 → ZRO (Arbitrum: ADA)
//   ID 60 → ADA — inactive
//   ID 62 → ARB — inactive
//   ID 64 → AVAX — inactive
//   ID 66 → AXS — inactive
//   ID 68 → BCH (Arbitrum: STX)
//   ID 70 → BERA — inactive
//   ID 72 → ENA (Arbitrum: LDO)
//   ID 74 → LINK (Arbitrum: FIL)
//   ID 76 → LTC — inactive
//   ID 78 → NEAR — inactive
//   ID 80 → ONDO (Arbitrum: DOT)
//   ID 82 → SKY — inactive
//   ID 84 → VIRTUAL — inactive
//   ID 86 → JUP (Arbitrum: ATOM)
//
// TradFi perps (added April 2026):
//   ID 88  → XAG (Silver)
//   ID 90  → WTI (Crude Oil)
//   ID 92  → EURUSD (Euro/USD)
//   ID 94  → GBPUSD (British Pound/USD)
//   ID 96  → USDJPY (USD/Japanese Yen)
//   ID 98  → QQQ (Invesco NASDAQ-100 ETF)
//   ID 100 → SPY (SPDR S&P 500 ETF)
//   ID 102 → AAPL (Apple)
//   ID 104 → AMZN (Amazon)
//   ID 106 → GOOGL (Alphabet)
//   ID 108 → META (Meta)
//   ID 110 → MSFT (Microsoft)
//   ID 112 → NVDA (NVIDIA)
//   ID 114 → TSLA (Tesla)

export const INK_PERP_METADATA_BY_PRODUCT_ID: Record<
  number,
  PerpProductMetadata
> = {
  2: BTC_PERP_METADATA,
  4: ETH_PERP_METADATA,
  8: SOL_PERP_METADATA,
  10: XRP_PERP_METADATA,
  14: BNB_PERP_METADATA,
  16: HYPE_PERP_METADATA,
  18: ZEC_PERP_METADATA,
  20: MON_PERP_METADATA,
  22: FARTCOIN_PERP_METADATA,
  24: SUI_PERP_METADATA,
  26: AAVE_PERP_METADATA,
  28: XAUT_PERP_METADATA,
  30: PUMP_PERP_METADATA,
  32: TAO_PERP_METADATA,
  34: XMR_PERP_METADATA,
  36: LIT_PERP_METADATA,
  38: KPEPE_PERP_METADATA,
  40: PENGU_PERP_METADATA,
  42: USELESS_PERP_METADATA,
  44: SKR_PERP_METADATA, // inactive
  46: UNI_PERP_METADATA,
  48: ASTER_PERP_METADATA,
  50: XPL_PERP_METADATA,
  52: DOGE_PERP_METADATA,
  54: WLFI_PERP_METADATA,
  56: KBONK_PERP_METADATA,
  58: ZRO_PERP_METADATA,
  60: ADA_PERP_METADATA, // inactive
  62: ARB_PERP_METADATA, // inactive
  64: AVAX_PERP_METADATA, // inactive
  66: AXS_PERP_METADATA, // inactive
  68: BCH_PERP_METADATA,
  70: BERA_PERP_METADATA, // inactive
  72: ENA_PERP_METADATA,
  74: LINK_PERP_METADATA,
  76: LTC_PERP_METADATA, // inactive
  78: NEAR_PERP_METADATA, // inactive
  80: ONDO_PERP_METADATA,
  82: SKY_PERP_METADATA, // inactive
  84: VIRTUAL_PERP_METADATA, // inactive
  86: JUP_PERP_METADATA,
  // ─── TradFi perps added by Nado (April 2026) ────────────────────────────────
  // Derived from https://gateway.prod.nado.xyz/v1/symbols (checked 2026-04-16)
  // Commodities
  88: createEquityPerpMetadata('XAG', ['commodity']),  // Silver
  90: createEquityPerpMetadata('WTI', ['commodity']),  // Crude Oil
  // Forex
  92: createEquityPerpMetadata('EURUSD', ['forex']),   // Euro / USD
  94: createEquityPerpMetadata('GBPUSD', ['forex']),   // British Pound / USD
  96: createEquityPerpMetadata('USDJPY', ['forex']),   // USD / Japanese Yen
  // Index ETFs (track equity indices — Nado categorizes under "Indices" tab, not "Equities")
  98: createEquityPerpMetadata('QQQ', ['index']),   // Invesco NASDAQ-100 ETF
  100: createEquityPerpMetadata('SPY', ['index']),  // SPDR S&P 500 ETF
  // Equities
  102: createEquityPerpMetadata('AAPL', ['equities']),
  104: createEquityPerpMetadata('AMZN', ['equities']),
  106: createEquityPerpMetadata('GOOGL', ['equities']),
  108: createEquityPerpMetadata('META', ['equities']),
  110: createEquityPerpMetadata('MSFT', ['equities']),
  112: createEquityPerpMetadata('NVDA', ['equities']),
  114: createEquityPerpMetadata('TSLA', ['equities']),
};

export const INK_TESTNET_PERP_METADATA_BY_PRODUCT_ID =
  INK_PERP_METADATA_BY_PRODUCT_ID;
